import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Grid,
    Link,
    Avatar,
    Tooltip,
    Snackbar
} from '@mui/material';
import { ContentCopyIcon } from 'icons/materialIcons';
import { getIconComponent } from 'utils/icons/iconUtils';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { getCardById } from 'apis/cardApi';
import { getUserById } from 'apis/authApi';
import { useAuth } from 'contexts/AuthContext';

const DetailColumn = ({ label, value, onCopy, isLink, linkType, avatar, connectedDate, cardFullName, connectedCards, formatDate, exportedToIcon }) => {
    const [showCopyTooltip, setShowCopyTooltip] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    if (!value && !avatar) return null;

    const handleCopy = () => {
        if (onCopy) {
            onCopy(value);
            setShowCopyTooltip(true);
            setTimeout(() => setShowCopyTooltip(false), 2000);
        }
    };

    const renderConnectedInfo = () => {
        if (connectedCards && connectedCards.length > 0) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        position: 'relative',
                        cursor: 'pointer',
                        width: 'fit-content'
                    }}
                >
                    {connectedCards.map((card, index) => (
                        <Tooltip
                            key={index}
                            title={
                                <div>
                                    <div>{card.name}</div>
                                    {card.connectedDate && (
                                        <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                            Connected on {formatDate(card.connectedDate)}
                                        </div>
                                    )}
                                </div>
                            }
                            placement="right"
                            arrow
                        >
                            <Avatar
                                src={card.avatar || avatarPlaceholder}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    transition: 'transform 0.2s ease-in-out',
                                    position: 'relative',
                                    zIndex: connectedCards.length - index,
                                    marginLeft: index > 0 ? '-12px' : 0,
                                    border: '2px solid white',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        transformOrigin: 'center',
                                        zIndex: 999
                                    }
                                }}
                            />
                        </Tooltip>
                    ))}
                </Box>
            );
        }

        // Fallback for single avatar (if needed)
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                    width: 'fit-content'
                }}
            >
                <Tooltip
                    title={
                        <div>
                            <div>{cardFullName || value}</div>
                            {connectedDate && (
                                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                                    Connected on {connectedDate}
                                </div>
                            )}
                        </div>
                    }
                    placement="right"
                    arrow
                >
                    <Avatar
                        src={avatar || avatarPlaceholder}
                        sx={{
                            width: 32,
                            height: 32,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.1)',
                                transformOrigin: 'center'
                            }
                        }}
                    />
                </Tooltip>
            </Box>
        );
    };

    const renderValue = () => {
        if (avatar || connectedCards) {
            return renderConnectedInfo();
        }
        if (isLink) {
            return (
                <Link
                    href={linkType === 'email' ? `mailto:${value}` : value}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: '#48befa',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {value}
                </Link>
            );
        }
        if (exportedToIcon) {
            // Render icon only for exported to field
            const IconComponent = getIconComponent(exportedToIcon);

            return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {IconComponent ? (
                        <IconComponent
                            width={28}
                            height={28}
                        />
                    ) : (
                        <Typography sx={{ fontSize: '12px', color: '#666' }}>
                            {exportedToIcon}
                        </Typography>
                    )}
                </Box>
            );
        }
        return (
            <Typography
                variant="body1"
                sx={{
                    color: '#242020',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px'
                }}
            >
                {value}
            </Typography>
        );
    };

    return (
        <Box sx={{ p: 2, pb: 1, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        color: '#828282',
                        mr: 1,
                        fontSize: '13px',
                        fontWeight: 500,
                        lineHeight: '18px'
                    }}
                >
                    {label}
                </Typography>
                {onCopy && value && (
                    <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                            color: '#828282',
                            p: 0,
                            mr: 1,
                            '&:hover': {
                                backgroundColor: 'transparent'
                            }
                        }}
                    >
                        <ContentCopyIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ mt: 1 }}>
                {renderValue()}
            </Box>
            <Snackbar
                open={showCopyTooltip}
                message="Copied!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        bgcolor: 'rgba(0, 0, 0, 0.8)',
                        color: 'white',
                        minWidth: 'auto',
                        padding: '6px 16px',
                        borderRadius: '4px',
                        fontSize: '14px'
                    }
                }}
            />
        </Box>
    );
};

const TeamLeadDetailCard = ({ lead }) => {
    const { authState } = useAuth();
    const [connectedMember, setConnectedMember] = useState(null);

    useEffect(() => {
        // Fetch the team member who owns the card this lead is assigned to
        const fetchTeamMember = async () => {
            try {
                // First try to get the card owner from cardId (for reassigned leads)
                if (lead?.cardId) {
                    console.log('🔍 Fetching card owner info for cardId:', lead.cardId);
                    const cardResponse = await getCardById(lead.cardId);

                    if (cardResponse && cardResponse.card) {
                        const card = cardResponse.card;

                        // Match leads table behavior: connected avatar comes from card first.
                        setConnectedMember({
                            name: card.cardName || card.contact_info?.fullName || 'Card',
                            avatar: card.profilePictureUrl || null,
                            connectedDate: lead.createdAt || lead.connectedDate
                        });

                        // Enrich with owner info when available; preserve card avatar priority.
                        if (card.userId) {
                            const cardOwnerUserId = card.userId;
                            console.log('🔍 Fetching team member info for card owner userId:', cardOwnerUserId);
                            const userData = await getUserById(cardOwnerUserId);

                            if (userData && (userData.user || userData.data)) {
                                const user = userData.user || userData.data;
                                setConnectedMember({
                                    name: user.fullName || user.email || card.cardName || card.contact_info?.fullName || 'Card',
                                    avatar: card.profilePictureUrl || user.profilePictureUrl || null,
                                    connectedDate: lead.createdAt || lead.connectedDate
                                });
                            }
                        }
                        return;
                    }
                }

                // Fallback: Use userId if cardId is not available (for older leads)
                if (lead?.userId) {
                    console.log('🔍 Fallback: Fetching team member info for userId:', lead.userId);
                    const userData = await getUserById(lead.userId);

                    if (userData && (userData.user || userData.data)) {
                        const user = userData.user || userData.data;
                        setConnectedMember({
                            name: user.fullName || user.email,
                            avatar: user.profilePictureUrl || null,
                            connectedDate: lead.createdAt || lead.connectedDate
                        });
                        console.log('✅ Team member info fetched:', user.fullName || user.email);
                        return;
                    }
                }
            } catch (error) {
                console.error('Error fetching team member info:', error);
                // Fallback - show basic info if fetch fails
                setConnectedMember({
                    name: 'Team Member',
                    avatar: null,
                    connectedDate: lead.createdAt || lead.connectedDate
                });
            }
        };

        if (lead) {
            fetchTeamMember();
        }
    }, [lead]);

    const handleCopy = (value) => {
        if (value) {
            navigator.clipboard.writeText(value);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Process all fields into a single array, similar to ContactDetailCard
    const getAllFields = () => {
        const standardFields = [];
        const customFieldsArray = [];

        // Add standard fields first
        if (lead?.leadName) standardFields.push({
            label: "Name",
            value: lead.leadName,
            onCopy: handleCopy
        });

        if (lead?.leadEmail) standardFields.push({
            label: "Email",
            value: lead.leadEmail,
            onCopy: handleCopy,
            isLink: true,
            linkType: "email"
        });

        if (lead?.leadJobTitle) standardFields.push({
            label: "Job Title",
            value: lead.leadJobTitle,
            onCopy: handleCopy
        });

        if (lead?.leadCompany) standardFields.push({
            label: "Company",
            value: lead.leadCompany,
            onCopy: handleCopy
        });

        if (lead?.crmLink) standardFields.push({
            label: "CRM Link",
            value: lead.crmLink,
            onCopy: handleCopy,
            isLink: true
        });

        if (lead?.createdAt) standardFields.push({
            label: "Date",
            value: formatDate(lead.createdAt),
            onCopy: handleCopy
        });

        // Add Connected With field after Date - Show team member who captured the lead
        // This will be displayed separately in a full-width row

        // Add team-specific fields
        if (lead?.exportedTo) standardFields.push({
            label: "Exported to",
            value: lead.exportedTo,
            exportedToIcon: lead.exportedToIcon
        });

        if (lead?.cardSource) standardFields.push({
            label: "Card Source",
            value: lead.cardSource
        });

        // Process custom fields if they exist
        if (lead?.customFields && typeof lead.customFields === 'object') {
            const isMapType = lead.customFields instanceof Map ||
                (typeof lead.customFields.get === 'function');

            if (isMapType) {
                for (const [key, field] of lead.customFields.entries()) {
                    const fieldValue = typeof field === 'object' && field !== null ? field.value : field;
                    const fieldLabel = typeof field === 'object' && field !== null && field.label ? field.label : key;
                    if (fieldValue && fieldValue.toString().trim()) {
                        customFieldsArray.push({
                            label: fieldLabel,
                            value: fieldValue,
                            onCopy: handleCopy
                        });
                    }
                }
            } else {
                Object.entries(lead.customFields).forEach(([key, field]) => {
                    const fieldValue = typeof field === 'object' && field !== null ? field.value : field;
                    const fieldLabel = typeof field === 'object' && field !== null && field.label ? field.label : key;

                    if (fieldValue && fieldValue.toString().trim()) {
                        customFieldsArray.push({
                            label: fieldLabel,
                            value: fieldValue,
                            onCopy: handleCopy
                        });
                    }
                });
            }
        }

        // Combine all fields in order (standard, custom)
        const allFields = [...standardFields, ...customFieldsArray];

        return allFields;
    };

    const allFields = getAllFields();

    // Helper to get initials from name
    const getInitials = (name) => {
        if (!name) return '';
        const nameParts = name.split(' ');
        return nameParts
            .map(part => part.charAt(0).toUpperCase())
            .slice(0, 2)
            .join('');
    };

    return (
        <Box sx={{ mt: 3, overflow: 'hidden' }}>
            <Grid container>
                {allFields.map((field, index) => (
                    <Grid
                        item
                        xs={12}
                        sm={field.label === 'Note' ? 12 : 6}
                        key={index}
                    >
                        <DetailColumn {...field} />
                    </Grid>
                ))}

                {/* Connected With - Always in separate full-width row */}
                {connectedMember && (
                    <Grid item xs={12}>
                        <Box sx={{ p: 2, pb: 1, width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: '#828282',
                                        mr: 1,
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        lineHeight: '18px'
                                    }}
                                >
                                    Connected with
                                </Typography>
                            </Box>
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    src={connectedMember.avatar || avatarPlaceholder}
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: '#e0e0e0'
                                    }}
                                >
                                    {!connectedMember.avatar && getInitials(connectedMember.name)}
                                </Avatar>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#242020',
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        lineHeight: '20px'
                                    }}
                                >
                                    {connectedMember.name}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default TeamLeadDetailCard;
